import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Skeleton,
  useMediaQuery,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import Swal from "sweetalert2";

import { NumberCellEditor } from "ag-grid-community";
import { fetchCustomerStatementJsonDynamic } from "../../../../data/DynamicServiceNew/jsondynamicservice";
import { graphqlQuery } from "../../../../data/Axios/DynamicService";
import { customerStatementReportQuery } from "../../../../data/Axios/queries";
import { tokens } from "../../../../theme";
import { customerservice ,reportsManagementUrl} from "../../../../config";
import { buildCustomerStatementHtml } from "./buildStatementHtml";
import { generateReport } from "../../../../data/DynamicTable/DynamicFunctions";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../../data/DynamicServiceNew/jsondynamicservice";

const DATE_PRESETS = [
  { value: "all", label: "All Time (Inception to date)" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "custom", label: "Custom Range" },
];

// Role groups that are always allowed to see the statement report sections,
// regardless of the "Hide Customer Statement for FO" setting. Any
// RoleGroupId NOT in this list is treated as an FO-equivalent role and is
// subject to the hide setting.
const ALWAYS_ALLOWED_ROLE_GROUP_IDS = [2, 3, 4];

const resolveDateRange = (preset, customStart, customEnd) => {
  const today = dayjs();
  switch (preset) {
    case "today":
      return {
        startDate: today.format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
      };
    case "yesterday": {
      const yesterday = today.subtract(1, "day");
      return {
        startDate: yesterday.format("YYYY-MM-DD"),
        endDate: yesterday.format("YYYY-MM-DD"),
      };
    }
    case "thisMonth":
      return {
        startDate: today.startOf("month").format("YYYY-MM-DD"),
        endDate: today.format("YYYY-MM-DD"),
      };
    case "lastMonth": {
      const lastMonth = today.subtract(1, "month");
      return {
        startDate: lastMonth.startOf("month").format("YYYY-MM-DD"),
        endDate: lastMonth.endOf("month").format("YYYY-MM-DD"),
      };
    }
    case "custom":
      return { startDate: customStart || null, endDate: customEnd || null };
    case "all":
    default:
      return { startDate: null, endDate: null };
  }
};

const CustomerStatementRight = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCustomer, setFilteredCustomer] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [datePreset, setDatePreset] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [legacyStartDate, setLegacyStartDate] = useState("");
  const [legacyEndDate, setLegacyEndDate] = useState("");
  const [isLegacyLoading, setIsLegacyLoading] = useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [runTour, setRunTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourKey, setTourKey] = useState(0); // Changing this forces re-mount

  // ── FO statement-access gating ────────────────────────────────────────
  // "Hide Customer Statement for FO" comes from customerSettings. When it's
  // true AND the logged-in user's RoleGroupId is NOT one of the always-
  // allowed role groups (1, 2, 3, 4), the report-generation sections are
  // hidden.
  const [hideStatementForFO, setHideStatementForFO] = useState(false);
  const [isCustomerSettingsLoaded, setIsCustomerSettingsLoaded] =
    useState(false);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const roleGroupId = parseInt(decodedToken.RoleGroupId);
  const isRestrictedRole = !ALWAYS_ALLOWED_ROLE_GROUP_IDS.includes(
    roleGroupId
  );

  // Should the report-generation sections (new + legacy) be shown?
  const canAccessStatementReport = !(isRestrictedRole && hideStatementForFO);

  const steps = [
    {
      target: ".search-input",
      content: "Enter a customer's phone number here to search.",
    },
    {
      target: ".customer-details",
      content: "Here you can view the customer's personal and loan details.",
    },
    ...(canAccessStatementReport
      ? [
          {
            target: ".start-date",
            content:
              "Choose the statement period: Today, Yesterday, This Month, Last Month, Custom Range, or All Time to include the customer's full history.",
          },
          {
            target: ".generate-report",
            content: "Click here to generate and view the customer's statement.",
          },
        ]
      : []),
  ];

  useEffect(() => {
    const fetchCustomerData = async () => {
      if (searchTerm.length < 10) {
        setFilteredCustomer(null);
        return;
      }

      setIsFetching(true);
      try {
        const variables = { customer: searchTerm, customerid: null }; // Fix variable names

        const response = await   fetchCustomerStatementJsonDynamic(
          "customerLoanAndSavings",
          null,
          variables,
          customerservice.uri
        );

        const parsedResults = JSON.parse(response.results);
        if (parsedResults.first_name != null) {
          setFilteredCustomer(parsedResults);
        } else {
          setFilteredCustomer(null);
          Swal.fire(
            "Customer Not Found",
            "No matching customer found.",
            "warning"
          );
        }
      } catch (error) {
        console.error("Error fetching customer data:", error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchCustomerData();
  }, [searchTerm]);

  // ── Fetch customer settings (e.g. "Hide Customer Statement for FO") ────
  useEffect(() => {
    async function fetchCustomerSettings() {
      try {
        const response = await fetchJsonDynamic(
          "customerSettings",
          null,
          null,
          customerservice.uri
        );

        if (response?.results) {
          const settings = JSON.parse(response.results);

          // Trim both the setting name and value since the source data can
          // contain trailing whitespace, e.g. "Hide Customer Statement for FO ".
          const hideStatementSetting = settings.find(
            (s) => s.Setting?.trim() === "Hide Customer Statement for FO "
          );

          setHideStatementForFO(
            hideStatementSetting?.Value?.trim()?.toLowerCase() === "true"
          );
        }
      } catch (error) {
        console.error("Error fetching customer settings:", error);
        setHideStatementForFO(false);
      } finally {
        setIsCustomerSettingsLoaded(true);
      }
    }
    fetchCustomerSettings();
  }, []);

  const handleGenerateReport = async () => {
    if (!filteredCustomer) return;
    if (!canAccessStatementReport) return;

    if (datePreset === "custom" && (!startDate || !endDate)) {
      Swal.fire({
        icon: "warning",
        title: "Select dates",
        text: "Please choose both a start and end date for a custom range, or pick All Time to include the customer's full history.",
      });
      return;
    }

    const newTab = window.open("", "_blank");
    if (newTab) {
      newTab.document.write("<p>Generating statement...</p>");
    }

    try {
      setIsLoading(true);
      const { startDate: queryStart, endDate: queryEnd } = resolveDateRange(
        datePreset,
        startDate,
        endDate
      );

      const query = customerStatementReportQuery(
        parseInt(filteredCustomer.id),
        queryStart,
        queryEnd
      );

      const response = await graphqlQuery(query, reportsManagementUrl.uri);

      if (response?.error) {
        newTab?.close();
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to generate the report. Please try again later.",
        });
        return;
      }

      const html = buildCustomerStatementHtml({
        rawResults: response?.results,
        generatedAt: new Date(),
      });

      if (newTab) {
        newTab.document.open();
        newTab.document.write(html);
        newTab.document.close();
      }
    } catch (error) {
      newTab?.close();
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while generating the report.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Legacy flow: posts to the old report-engine service (ReportTypeId 42),
  // which returns a fully pre-rendered HTML report instead of raw GraphQL data.
  const handleGenerateLegacyReport = async () => {
    if (!filteredCustomer) return;
    if (!canAccessStatementReport) return;

    const reportTypeId = 42;
    const reportType = "Customer Statement";

    const newTab = window.open("", "_blank");
    if (newTab) {
      newTab.document.write("<p>Loading...</p>");
    }

    try {
      setIsLegacyLoading(true);
      const mutationData = {
        ReportTypeId: reportTypeId,
        ReportType: reportType,
        CustomerId: parseInt(filteredCustomer.id),
        StartDate: legacyStartDate,
        EndDate: legacyEndDate,
      };

      const response = await generateReport(
        mutationData,
        reportsManagementUrl.generate_report_uri,
        setIsLegacyLoading
      );

      if (response.ok) {
        const htmlContent = await response.text();
        if (newTab) {
          newTab.document.open();
          newTab.document.write(htmlContent);
          newTab.document.close();
        }
      } else {
        newTab?.close();
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to generate the report. Please try again later.",
        });
      }
    } catch (error) {
      newTab?.close();
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while generating the report.",
      });
    } finally {
      setIsLegacyLoading(false);
    }
  };

  const fields = filteredCustomer
    ? [
        {
          label: "CustomerID",
          value: `${filteredCustomer.id || "N/A"}`,
        },
        {
          label: "LoanID",
          value: `${filteredCustomer.loanid || "N/A"}`,
        },

        { label: "First Name", value: filteredCustomer.first_name || "N/A" },
        { label: "Last Name", value: filteredCustomer.last_name || "N/A" },
        {
          label: "Mobile Number",
          value: filteredCustomer.mobile_number || "N/A",
        },

        {
          label: "Booking Date",
          value: filteredCustomer.date_created
            ? new Date(filteredCustomer.date_created).toLocaleDateString()
            : "N/A",
        },
        {
          label: "Loan Disbursed Date",
          value: filteredCustomer?.Loan_disbursed_date
            ? new Date(
                filteredCustomer?.Loan_disbursed_date
              ).toLocaleDateString()
            : "N/A",
        },
        {
          label: "Loan Amount",
          value: `KSH ${filteredCustomer.active_loan || 0.0}`,
        },
        { label: "OLB", value: `KSH ${filteredCustomer.olb || 0.0}` },
        {
          label: "Arrears",
          value: `KSH ${filteredCustomer.loan_arrears || 0.0}`,
        },
        { label: "Days Overdue", value: filteredCustomer.days_overdue || 0 },
        {
          label: "BBF Account Balance",
          value: `KSH ${filteredCustomer.bbf_account_balance || 0.0}`,
        },
      ]
    : [];

  return (
    <Box sx={{ padding: 4 }}>
        {/* Joyride Component */}
     <Joyride
        key={tourKey} // 🔑 Ensures full reset
        steps={steps}
        run={runTour}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        styles={{
          options: {
            primaryColor: "#1976d2",
            zIndex: 10000,
          },
        }}
        callback={(data) => {
          const { action, index, status, type } = data;

          if (
            status === "finished" ||
            status === "skipped" ||
            (action === "close" && type === "tour:end")
          ) {
            setRunTour(false);
            setStepIndex(0);
          } else if (type === "step:after") {
            setStepIndex(index + 1);
          }
        }}
      />

      <Button
        onClick={() => {
          setTourKey((prev) => prev + 1); // Force re-render
          setStepIndex(0);               // Start from first step
          setRunTour(true);              // Start the tour
        }}
      >
        Start Tour
      </Button>

      <Typography variant="h4">Customer Statement</Typography>
      <TextField
        label="Search Customer by phone number"
        placeholder="Enter phone number"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="search-input" // ✅ Add ID for tour
        InputProps={{
          endAdornment: isFetching && (
            <CircularProgress size={24} sx={{ marginRight: 2 }} />
          ),
        }}
      />

      {filteredCustomer && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              padding: { xs: 1, sm: 2 },
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
            <Box className="customer-details">
              <Typography 
              className="customer-details"
                variant="h6"
                gutterBottom
                sx={{
                  textAlign: { xs: "center", sm: "left" },
                  mb: 3,
                  fontSize: "1rem",
                }}
              >
                Customer Details
              </Typography>
              {isSmallScreen ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    background: colors.primary[200],
                    borderRadius: 2,
                    padding: 2,
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  {fields.map((field, index) => (
                    <Grid
                      container
                      key={index}
                      sx={{
                        borderBottom:
                          index !== fields.length - 1
                            ? "1px solid #ccc"
                            : "none",
                        py: 1,
                      }}
                    >
                      <Grid item xs={6}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold" }}
                        >
                          {field.label}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ textAlign: "right" }}>
                          {field.value?.toLocaleString() || field.value  }
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    background: `${colors.primary[200]} !important`,
                    borderRadius: 2,
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                    overflowX: "auto",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        {fields.map((field, index) => (
                          <TableCell
                            key={index}
                            sx={{
                              background: colors.primary[400],
                              fontSize: "0.65rem",
                              fontWeight: "bold",
                            }}
                          >
                            {field.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        {fields.map((field, index) => (
                          <TableCell key={index} sx={{ fontSize: "0.73rem" }}>
                            {field.value?.toLocaleString() || field.value  }
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Report-generation sections: hidden for any RoleGroupId not in
                [1, 2, 3, 4] when customerSettings' "Hide Customer Statement
                for FO" is true. */}
            {!isCustomerSettingsLoaded ? null : canAccessStatementReport ? (
              <>
                <Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      textAlign: { xs: "center", sm: "left" },
                      mb: 2,
                      fontSize: "1rem",
                    }}
                  >
                    Generate Customer Statement
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      background: colors.primary[200],
                      borderRadius: 2,
                      padding: { xs: 1.5, sm: 2 },
                      maxWidth: "300px",
                    }}
                  >
                    <Alert severity="info" sx={{ fontSize: "0.75rem" }}>
                      Choose a period for the statement: Today, Yesterday, This
                      Month, Last Month, or a Custom Range. Leave it on "All Time"
                      to include the customer's entire transaction history.
                    </Alert>
                    <FormControl fullWidth size="small" className="start-date">
                      <InputLabel id="date-preset-label">
                        Statement Period
                      </InputLabel>
                      <Select
                        labelId="date-preset-label"
                        label="Statement Period"
                        value={datePreset}
                        onChange={(e) => setDatePreset(e.target.value)}
                        sx={{ background: colors.primary[400], borderRadius: 1 }}
                      >
                        {DATE_PRESETS.map((preset) => (
                          <MenuItem key={preset.value} value={preset.value}>
                            {preset.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {datePreset === "custom" && (
                      <>
                        <TextField
                          label="Start Date"
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          sx={{ background: colors.primary[400], borderRadius: 1 }}
                        />
                        <TextField
                          label="End Date"
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          className="end-date"
                          sx={{ background: colors.primary[400], borderRadius: 1 }}
                        />
                      </>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleGenerateReport}
                      disabled={isLoading}
                      fullWidth
                      size="small"
                      className="generate-report"
                      sx={{
                        background: colors.blueAccent[500],
                        "&:hover": { background: colors.blueAccent[600] },
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        "Generate Report"
                      )}
                    </Button>
                  </Box>
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      textAlign: { xs: "center", sm: "left" },
                      mb: 2,
                      fontSize: "1rem",
                    }}
                  >
                    Legacy Statement (Report Engine)
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      background: colors.primary[200],
                      borderRadius: 2,
                      padding: { xs: 1.5, sm: 2 },
                      maxWidth: "300px",
                    }}
                  >
                    <Alert severity="warning" sx={{ fontSize: "0.75rem" }}>
                      This uses the old report engine. Prefer the statement above
                      unless you specifically need the legacy layout.
                    </Alert>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={legacyStartDate}
                      onChange={(e) => setLegacyStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      sx={{ background: colors.primary[400], borderRadius: 1 }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={legacyEndDate}
                      onChange={(e) => setLegacyEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      sx={{ background: colors.primary[400], borderRadius: 1 }}
                    />
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleGenerateLegacyReport}
                      disabled={isLegacyLoading}
                      fullWidth
                      size="small"
                    >
                      {isLegacyLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        "Generate Legacy Report"
                      )}
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <Alert severity="info" sx={{ maxWidth: 300 }}>
                You don't have permission to generate customer statements.
              </Alert>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default CustomerStatementRight;